import { PageHeader } from 'components/PageHeader';
import { PageToolbar } from 'components/PageToolbar';
import React from 'react';
import { View } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectTaskPaginationDetails,
  selectTasksAssignedFilterOrganisations,
  selectTasksAssignedFilterProperties,
  selectTasksAssignedFilterDueDateFrom,
  selectTasksAssignedFilterDueDateTo,
  selectTasksAssignedFilterStatuses,
  selectTasksAssignedIsLoading,
  selectTasksAssignedFilterOptions,
  selectTasksAssignedSearch,
} from 'store/tasks/selectors';
import { ButtonGroup } from 'components/ButtonGroup';
import { IconButton } from 'components/IconButton';
import { ICONS } from 'components/Icon';
import {
  doTasksAssignedFilterOpenModal,
  doTasksAssignedLoad,
} from 'store/tasks/actions';
import { TASK_SEARCH_MAX_LENGTH, TaskStatusType } from '@riskbase/types';
import { formatNumber } from 'lib/pluralise';
import { areTasksAssignedStatusIdsDefault } from 'types/Tasks';

type Props = {
  onSearch: (s: string) => void;
};

export const TasksAssignedHeader: React.FC<Props> = ({ onSearch }) => {
  const dispatch = useDispatch();
  const tasks = useSelector(selectTaskPaginationDetails);
  const isLoading = useSelector(selectTasksAssignedIsLoading);
  const selectedOrganisations = useSelector(
    selectTasksAssignedFilterOrganisations,
  );
  const selectedProperties = useSelector(selectTasksAssignedFilterProperties);
  const dueDateFrom = useSelector(selectTasksAssignedFilterDueDateFrom);
  const dueDateTo = useSelector(selectTasksAssignedFilterDueDateTo);
  const selectedStatusIds = useSelector(selectTasksAssignedFilterStatuses);
  const statusOptions = useSelector(selectTasksAssignedFilterOptions).statuses;
  const search = useSelector(selectTasksAssignedSearch);

  const summaryText = React.useMemo(() => {
    const summaryBreakdown = tasks.statusTotals.reduce(
      (acc, task) => {
        if (task.statusType === TaskStatusType.cancelled) {
          acc.cancelled = task.total;
        } else if (task.statusType === TaskStatusType.completed) {
          acc.completed = task.total;
        } else if (task.statusType === TaskStatusType.notStarted) {
          acc.notStarted = task.total;
        } else {
          acc.inProgress = task.total;
        }
        return acc;
      },
      {
        notStarted: 0,
        inProgress: 0,
        completed: 0,
        cancelled: 0,
      },
    );

    return [
      summaryBreakdown.notStarted
        ? formatNumber(summaryBreakdown.notStarted) + ' Not Started'
        : false,
      summaryBreakdown.inProgress
        ? formatNumber(summaryBreakdown.inProgress) + ' In Progress'
        : false,
      summaryBreakdown.completed
        ? formatNumber(summaryBreakdown.completed) + ' Completed'
        : false,
    ]
      .filter(Boolean)
      .join(' • ');
  }, [tasks]);

  const handleReload = React.useCallback(() => {
    dispatch(doTasksAssignedLoad({}));
  }, [dispatch]);

  const handleOpenFiltersModal = React.useCallback(() => {
    dispatch(doTasksAssignedFilterOpenModal());
  }, [dispatch]);

  const filterIsTriggered =
    selectedOrganisations.length > 0 ||
    selectedProperties.length > 0 ||
    !!dueDateFrom ||
    !!dueDateTo ||
    !areTasksAssignedStatusIdsDefault(selectedStatusIds, statusOptions) ||
    search.trim().length > 0;

  return (
    <View>
      <PageHeader title="My Tasks" subtitle={summaryText} showSidebarButton />
      <PageToolbar
        onSearch={onSearch}
        searchValue={search}
        searchOnSubmit
        searchMaxLength={TASK_SEARCH_MAX_LENGTH}
        searchPlaceholder="Search tasks..."
      >
        <ButtonGroup>
          <IconButton
            icon={ICONS.RotateRightLight}
            onPress={handleReload}
            disabled={isLoading}
          />
          <IconButton
            icon={ICONS.SlidersUpLight}
            onPress={handleOpenFiltersModal}
            showBadge={filterIsTriggered}
            disabled={isLoading}
          />
        </ButtonGroup>
      </PageToolbar>
    </View>
  );
};
